import React, { Component, ErrorInfo, ReactNode } from "react";
import { captureError } from "../lib/analytics";

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  errorId: string | null;
};

// Top-level error boundary. Without this, any uncaught render error
// renders a blank white page — which at marketing volume will lose
// many buyers to the long tail of edge-case browsers/extensions.
// Shows a Hebrew RTL fallback with a reload button + support email.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, errorId: null };

  static getDerivedStateFromError(_error: Error): State {
    return {
      hasError: true,
      errorId: Math.random().toString(36).slice(2, 10).toUpperCase(),
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureError(error, {
      boundary: "root",
      errorId: this.state.errorId ?? "",
      componentStack: info.componentStack ?? "",
    });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleHome = (): void => {
    window.location.href = "/";
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        dir="rtl"
        style={{
          minHeight: "100vh",
          background: "#FAF7F2",
          color: "#2D1B10",
          fontFamily: "'Heebo', system-ui, sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <div
          style={{
            maxWidth: 520,
            background: "#FFFDF9",
            border: "1px solid #E8DCC8",
            borderRadius: 16,
            padding: "32px 28px",
            boxShadow: "0 4px 24px rgba(92,61,46,0.10)",
            textAlign: "right",
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 8 }}>😔</div>
          <h1
            style={{
              fontFamily: "'Frank Ruhl Libre', serif",
              fontSize: 24,
              fontWeight: 700,
              color: "#5C3D2E",
              marginBottom: 12,
            }}
          >
            משהו השתבש
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: "#5C3D2E", marginBottom: 20 }}>
            סליחה — נתקלנו בתקלה לא צפויה. ניסיון לרענון בדרך כלל פותר את העניין.
            אם זה ממשיך, נשמח לשמוע במייל ונחזור אליכם:
          </p>
          <p style={{ fontSize: 14, marginBottom: 24 }}>
            <a
              href="mailto:shlomo@eiforall.com?subject=תקלה%20באתר"
              style={{ color: "#A0704A", textDecoration: "underline", fontWeight: 500 }}
            >
              shlomo@eiforall.com
            </a>
          </p>
          {this.state.errorId && (
            <p style={{ fontSize: 12, color: "#8B7355", marginBottom: 20 }}>
              קוד תקלה: <code style={{ fontFamily: "monospace" }}>{this.state.errorId}</code>{" "}
              (יעזור לנו לאתר את הבעיה אם תצרפו אותו במייל)
            </p>
          )}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button
              onClick={this.handleReload}
              style={{
                background: "#5C3D2E",
                color: "white",
                border: "none",
                borderRadius: 40,
                padding: "10px 22px",
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              רענון העמוד
            </button>
            <button
              onClick={this.handleHome}
              style={{
                background: "#F3EDE3",
                color: "#5C3D2E",
                border: "1px solid #E8DCC8",
                borderRadius: 40,
                padding: "10px 22px",
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              חזרה לעמוד הראשי
            </button>
          </div>
        </div>
      </div>
    );
  }
}
